import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../../../../../Admin/constants/adminColors';
import SectionCard from '../SectionCard';
import { WeeklyMetricRow, WeeklyMetricCell } from '../../types/beginActivity.common.types';
import { LastTwelveWeeksProps } from './LastTwelveWeeks.types';
import styles from './LastTwelveWeeks.styles';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(value: number | null, unit: string): string {
  if (value === null) return '—';
  if (unit === '%') return `${(value * 100).toFixed(1)}%`;
  if (unit === 'sec') return value.toFixed(1);
  if (unit === 'cases') return value.toLocaleString();
  return value.toFixed(2);
}

function fmtTarget(target: number | null, unit: string): string {
  if (target === null) return '';
  if (unit === '%') return `Target: ${(target * 100).toFixed(1)}%`;
  if (unit === 'cases') return `Target: ${target.toLocaleString()}`;
  return `Target: ${target.toFixed(target < 10 ? 1 : 0)}`;
}

function fmtAverage(row: WeeklyMetricRow): string {
  const vals = row.cells
    .filter((c) => c.actual !== null)
    .map((c) => c.actual as number);
  if (!vals.length) return '—';
  const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
  return fmt(avg, row.unit);
}

// ─── Single week cell ─────────────────────────────────────────────────────────
function WeekCell({ cell, unit, flexFill }: { cell: WeeklyMetricCell; unit: string; flexFill?: boolean }) {
  const isPending = cell.actual === null;

  const containerStyle: any[] = [styles.weekCell];
  if (flexFill) containerStyle.push(styles.weekCellFlex);
  if (isPending) containerStyle.push(styles.weekCellPending);
  else if (cell.state === 'met') containerStyle.push(styles.weekCellMet);
  else if (cell.state === 'miss') containerStyle.push(styles.weekCellMiss);
  if (cell.state === 'current') containerStyle.push(styles.weekCellCurrent);

  const valueStyle =
    isPending ? styles.weekCellValuePending
    : cell.state === 'met' ? styles.weekCellValueMet
    : cell.state === 'miss' ? styles.weekCellValueMiss
    : styles.weekCellValueNeutral;

  const subColored = !isPending && (cell.state === 'met' || cell.state === 'miss');

  return (
    <View style={containerStyle}>
      <Text style={[styles.weekCellValue, valueStyle]}>
        {isPending ? '—' : fmt(cell.actual, unit)}
      </Text>
      {isPending ? (
        <Text style={styles.weekCellSub}>Not worked yet</Text>
      ) : (
        cell.target !== null && (
          <Text style={[styles.weekCellSub, subColored ? styles.weekCellSubColored : null]}>
            {fmtTarget(cell.target, unit)}
          </Text>
        )
      )}
    </View>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const LastTwelveWeeks: React.FC<LastTwelveWeeksProps> = ({
  title = 'Last Twelve Weeks',
  rows,
  periodRows,
  periodTitle = 'Last Twelve Periods',
  isLoading = false,
  activityType,
  onTrendPress,
}) => {
  const [viewMode, setViewMode] = useState<'week' | 'period'>('week');
  const isPulseCheck = activityType === 'pulse_check';
  const activeRows = viewMode === 'period' ? (periodRows ?? rows) : rows;
  const activeTitle = viewMode === 'period' ? periodTitle : title;
  const weekLabels = activeRows.length > 0 ? activeRows[0].cells.map((c) => c.weekLabel) : [];

  return (
    <SectionCard
      title={activeTitle}
      description={
        isPulseCheck
          ? 'Blue means the annual site target was met. Red means the target was missed.'
          : 'Performance context before the activity observation begins'
      }
      subtitle={
        <View style={styles.toggleRow}>
          <TouchableOpacity
            style={[styles.togglePill, viewMode === 'week' && styles.togglePillActive]}
            onPress={() => setViewMode('week')}
          >
            <Text style={[styles.togglePillText, viewMode === 'week' && styles.togglePillTextActive]}>Week</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.togglePill, viewMode === 'period' && styles.togglePillActive]}
            onPress={() => setViewMode('period')}
          >
            <Text style={[styles.togglePillText, viewMode === 'period' && styles.togglePillTextActive]}>Period</Text>
          </TouchableOpacity>
        </View>
      }
    >
      {/* Loading */}
      {isLoading && (
        <View style={styles.stateContainer}>
          <ActivityIndicator size="small" color={AdminColors.blue700} />
          <Text style={styles.stateText}>Loading metrics…</Text>
        </View>
      )}

      {/* Empty */}
      {!isLoading && activeRows.length === 0 && (
        <View style={styles.stateContainer}>
          <MaterialIcons name="bar-chart" size={28} color={AdminColors.gray200} />
          <Text style={styles.stateText}>No metrics available for this employee.</Text>
        </View>
      )}

      {/* ── Split-panel table ── */}
      {!isLoading && activeRows.length > 0 && (
        <View style={styles.tableContainer}>
          {/* LEFT: frozen Metric column only */}
          <View style={styles.leftPanel}>
            <View style={styles.theadRow}>
              <View style={styles.thMetricCell}>
                <Text style={styles.thText}>Metric</Text>
              </View>
            </View>
            {activeRows.map((row) => (
              <View key={row.metricName} style={styles.dataRow}>
                <View style={styles.tdMetricCell}>
                  <TouchableOpacity
                    onPress={() => onTrendPress?.(row.metricName)}
                    hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                    style={styles.trendButton}
                  >
                    <Text style={styles.metricNameText}>{row.metricName}</Text>
                    <MaterialIcons name="trending-up" size={13} color={AdminColors.blue700} />
                  </TouchableOpacity>
                  <Text style={styles.metricUnitText}>{row.unit}</Text>
                </View>
              </View>
            ))}
          </View>

          {/* RIGHT: scrollable columns (12-Wk Avg + Weeks Met for pulse_check, then week cols) */}
          {isPulseCheck ? (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator
              style={styles.rightScroll}
              contentContainerStyle={styles.rightScrollContent}
            >
              <View>
                <View style={styles.theadRow}>
                  <View style={styles.thSummaryCell}>
                    <Text style={styles.thText}>{`12-Wk\nAvg`}</Text>
                  </View>
                  <View style={styles.thSummaryCell}>
                    <Text style={styles.thText}>{`Weeks\nMet`}</Text>
                  </View>
                  {weekLabels.map((lbl) => (
                    <View key={lbl} style={styles.thWeekCell}>
                      <Text style={styles.thText}>{lbl}</Text>
                    </View>
                  ))}
                </View>
                {activeRows.map((row) => (
                  <View key={row.metricName} style={styles.dataRow}>
                    <View style={styles.tdSummaryCell}>
                      <Text style={styles.summaryValue}>{fmtAverage(row)}</Text>
                    </View>
                    <View style={styles.tdSummaryCell}>
                      <Text style={styles.summaryValue}>{row.weeksMet}/{row.weeksTotal}</Text>
                      <Text style={styles.summaryPct}>
                        {row.weeksTotal > 0 ? Math.round((row.weeksMet / row.weeksTotal) * 100) : 0}%
                      </Text>
                    </View>
                    {row.cells.map((cell) => (
                      <WeekCell key={cell.weekLabel} cell={cell} unit={row.unit} />
                    ))}
                  </View>
                ))}
              </View>
            </ScrollView>
          ) : (
            // No 12-Wk Avg / Weeks Met columns here, so the week columns flex to fill the
            // full table width instead of leaving a gap on the right (no scroll needed).
            <View style={styles.rightFlexPanel}>
              <View style={styles.theadRow}>
                {weekLabels.map((lbl) => (
                  <View key={lbl} style={[styles.thWeekCell, styles.thWeekCellFlex]}>
                    <Text style={styles.thText}>{lbl}</Text>
                  </View>
                ))}
              </View>
              {activeRows.map((row) => (
                <View key={row.metricName} style={styles.dataRow}>
                  {row.cells.map((cell) => (
                    <WeekCell key={cell.weekLabel} cell={cell} unit={row.unit} flexFill />
                  ))}
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      {/* ── Legend ── */}
      {!isLoading && activeRows.length > 0 && (
        <View style={styles.legendRow}>
          <View style={styles.legendItem}>
            <View style={[styles.legendSquare, styles.legendSquareMet]} />
            <Text style={styles.legendText}>Target met</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendSquare, styles.legendSquareMiss]} />
            <Text style={styles.legendText}>Target missed</Text>
          </View>
          <Text style={styles.legendDisclaimer}>
            Targets are supplied automatically from the employee site profile.
          </Text>
        </View>
      )}
    </SectionCard>
  );
};

export default LastTwelveWeeks;
